import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  Inject,
  ViewChildren,
  QueryList,
  Input,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DynamicOptionsService } from '../../dynamic-options.service';

export interface FilterRule {
  condition?: 'AND' | 'OR' | 'AND NOT' | 'OR NOT';
  field: string;
  type: string;
  operator: string;
  value: any;
}

export interface FilterGroup {
  condition: 'AND' | 'OR' | 'AND NOT' | 'OR NOT';
  rules: (FilterRule | FilterGroup)[];
}

@Component({
  selector: 'app-filter-builder',
  templateUrl: './filter-builder.component.html',
  styleUrls: ['./filter-builder.component.css'],
})
export class FilterBuilderComponent implements OnInit {
  @Input() filter!: FilterGroup;
  @Input() isNested = false;
  @Output() filterApplied = new EventEmitter<any>();
  @ViewChildren(MatSelect) allSelects!: QueryList<MatSelect>;

  textOperators = ['is', 'is not', 'contains', 'does not contain'];
  numberOperators = ['=', '!=', '>', '<'];
  dateOperators = ['is', 'before', 'after'];
  booleanOperators = ['is', 'is not'];
  dropdownOperators = ['is', 'is not'];

  dropdownStreams: { [key: string]: Observable<any[]> } = {};
  isLoadingMap: { [key: string]: Observable<boolean> } = {};
  searchControl = new FormControl('');
  activeDropdownKey = '';

  public dynamicFilterField: any[];

  // Map legacy Dentassist filter types to FilterBuilder types
  private typeMap: Record<string, string> = {
    string: 'text',
    date_range: 'date',
    radio_option: 'select',
    dropdown: 'dropdown',
    multi_select: '', // skip
  };

  constructor(
    public dialogRef: MatDialogRef<FilterBuilderComponent>,
    @Inject(MAT_DIALOG_DATA) data: any,
    private dynamicOptionsService: DynamicOptionsService
  ) {
    // Normalize legacy filter fields and remove order_by/multi_select entries
    this.dynamicFilterField = (data.filterFields || [])
      .filter((f: any) => f.type !== 'multi_select')
      .map((f: any) => ({
        ...f,
        type: this.typeMap[f.type] || f.type,
        model: f.model || f.model_name || f.name,
      }));
  }

  ngOnInit() {
    if (!this.filter || !this.filter.rules) {
      this.filter = { condition: 'AND', rules: [] };
    }

    if (this.filter.rules.length === 0) {
      this.addRule(this.filter);
    }

    this.searchControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe((term) => {
        if (term) {
          const columnName = this.getColumnName(this.activeDropdownKey);
          this.dynamicOptionsService.search(
            this.activeDropdownKey,
            term,
            columnName
          );
        }
      });
  }

  getColumnName(dropdownKey: string) {
    return dropdownKey.replace('dropdown_', '');
  }

  ngAfterViewInit(): void {
    this.allSelects.changes.subscribe((dropdowns: QueryList<MatSelect>) => {
      dropdowns.forEach((select) => {
        select.openedChange.subscribe((opened) => {
          if (opened) {
            setTimeout(() => {
              const panel = select.panel?.nativeElement;
              const dropdownKey = this.getDropdownKeyFromSelect(select);
              if (panel && dropdownKey) {
                panel.addEventListener('scroll', () =>
                  this.onScroll(dropdownKey, panel)
                );
                this.activeDropdownKey = dropdownKey;
              }
            });
          }
        });
      });
    });
  }

  onScroll(dropdownKey: string, panel: HTMLElement): void {
    const bottomReached =
      panel.scrollHeight - panel.scrollTop === panel.clientHeight;
    if (bottomReached) {
      this.dynamicOptionsService.loadNext(dropdownKey);
    }
  }

  getDropdownKeyFromSelect(select: MatSelect): string | null {
    return select._elementRef.nativeElement.getAttribute('data-key');
  }

  addRule(filter: FilterGroup) {
    const newRule: FilterRule = {
      condition: 'OR',
      field: '',
      type: '',
      operator: '',
      value: '',
    };
    filter.rules.push(newRule);
  }

  removeRule(filter: FilterGroup, index: number) {
    filter.rules.splice(index, 1);
  }

  updateCondition(newCondition: string, filter: FilterGroup, index: number) {
    const rule = filter.rules[index];
    if (this.isFilterRule(rule)) {
      rule.condition = newCondition as FilterRule['condition'];
    }
  }

  updateRuleType(rule: FilterRule) {
    const column = this.dynamicFilterField.find(
      (c) => c.name === rule.field
    );
    if (column) {
      rule.type = column.type;
      rule.operator = this.getDefaultOperator(column.type);
      rule.value = '';
    }

    if (column?.type === 'dropdown') {
      const key = `dropdown_${column.name}`;
      const model = column.model || column.name;
      this.dynamicOptionsService.init(key, model);
      this.dropdownStreams[column.name] =
        this.dynamicOptionsService.getOptions$(key);
      this.isLoadingMap[column.name] =
        this.dynamicOptionsService.getIsLoading$(key);
    }
  }

  getDefaultOperator(type: string): string {
    switch (type) {
      case 'text':
        return 'is';
      case 'number':
        return '=';
      case 'date':
        return 'is';
      case 'boolean':
        return 'is';
      case 'dropdown':
        return 'is';
      case 'select':
        return 'is';
      default:
        return 'is';
    }
  }

  submitFilter() {
    const flatRules = this.filter.rules.map((rule, i) => {
      if (this.isFilterRule(rule)) {
        return {
          condition: rule.condition || (i === 0 ? undefined : 'AND'),
          field: rule.field,
          type: rule.type,
          operator: rule.operator,
          value: rule.value,
        };
      }
      return rule;
    });

    this.filterApplied.emit(flatRules);
    this.dialogRef.close(flatRules);
  }

  isFilterRule(obj: any): obj is FilterRule {
    return obj && 'field' in obj;
  }

  isFilterGroup(obj: any): obj is FilterGroup {
    return obj && 'condition' in obj && 'rules' in obj;
  }

  getStaticValues(fieldName: string): { label: string; value: any }[] {
    const col = (this.dynamicFilterField || []).find(
      (c: any) => c.name === fieldName
    );
    return col && Array.isArray(col.values) ? col.values : [];
  }

  getDisplayKey(fieldName: string): string {
    const col = (this.dynamicFilterField || []).find(
      (c: any) => c.name === fieldName
    );
    if (col && col.displayField) return col.displayField;
    return 'name';
  }

  getOptionLabel(fieldName: string, option: any): string {
    if (!option) return '';
    const key = this.getDisplayKey(fieldName);
    const val = option?.[key];
    return val !== undefined && val !== null ? String(val) : '';
  }
}
